
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ConnectionHealthMonitor, ConnectionHealthStatus } from '@/services/connectionHealthMonitor';
import { TokenRefreshService } from '@/services/tokenRefreshService';

export const useConnectionHealth = (autoCheck: boolean = true) => {
  const { session } = useAuth();
  const [healthStatus, setHealthStatus] = useState<ConnectionHealthStatus | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const checkHealth = useCallback(async () => {
    if (!session?.user?.id) return;

    setIsChecking(true);
    setLastError(null);

    try {
      const health = await ConnectionHealthMonitor.checkConnectionHealth(session.user.id);
      setHealthStatus(health);
    } catch (error) {
      console.error('Health check failed:', error);
      setLastError(error instanceof Error ? error.message : 'Health check failed');
    } finally {
      setIsChecking(false);
    }
  }, [session?.user?.id]);

  const refreshTokens = useCallback(async () => {
    if (!session?.user?.id) return false;

    try {
      const result = await TokenRefreshService.refreshTokensIfNeeded(session.user.id);
      
      if (result.success) {
        // Re-check health after successful refresh
        await checkHealth();
        return true;
      } else if (result.needsReauth) {
        setLastError('Your Microsoft account connection has expired. Please reconnect.');
        return false;
      } else {
        setLastError(result.error || 'Token refresh failed');
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      setLastError(error instanceof Error ? error.message : 'Token refresh failed');
      return false;
    }
  }, [session?.user?.id, checkHealth]);

  // Auto-check health on mount and periodically if enabled
  useEffect(() => {
    if (!autoCheck || !session?.user?.id) return;

    checkHealth();

    // Check health every 5 minutes
    const interval = setInterval(checkHealth, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [autoCheck, checkHealth, session?.user?.id]);

  return {
    healthStatus,
    isChecking,
    lastError,
    checkHealth,
    refreshTokens,
  };
};
